/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { sortBy, sortedUniq } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  gameLogger,
  selectCurrentHorse,
  selectCurrentHorseCardDeck,
  selectCurrentState,
  selectOwnHorseRemovedCards,
  selectPlayerId,
  selectRound,
  useSelector,
} from '~/app';
import { Modal } from '~/components/Modal';
import { sendAddBonusCards, sendMergeCards } from '~/overlay/actions';
import { CardValue } from '~/overlay/bar-bottom/Card';
import { ReactComponent as ButtonSuccess } from '~/overlay/button-icons/button-success.svg';
import Turfmaster from '~/server/src/tmmodel';
import { useKeyPress } from '~/utils';
import { cn } from '~/utils/styles';

import CardRow from './CardRow';
import ViewSwitchButton from './ViewSwitchButton';

interface MergeViewProps {
  debugMode?: boolean;
  /** When true, never show merge modal (spectators must not see cards). */
  isSpectatorMode?: boolean;
  /** When true, never show merge modal (viewing in-game history). */
  isHistoryMode?: boolean;
}

const MergeView = ({
  debugMode = false,
  isSpectatorMode = false,
  isHistoryMode = false,
}: MergeViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const currentState = useSelector(selectCurrentState);
  const round = useSelector(selectRound);
  const playerId = useSelector(selectPlayerId);
  const { cards, bonuscards } = useSelector(selectCurrentHorseCardDeck);
  const currentHorse = useSelector(selectCurrentHorse);
  const currentBonusCards = useMemo(
    () =>
      currentHorse &&
      bonuscards === -1 &&
      bonuscards &&
      cards?.length === Turfmaster.rounds + 1 &&
      cards[Turfmaster.rounds]
        ? cards[Turfmaster.rounds]
        : [],
    [currentHorse, cards, bonuscards],
  );

  const ownHorseRemovedCards = useSelector(selectOwnHorseRemovedCards);

  const currentPlayerId = currentHorse?.playerId ?? -1;

  const [added, setAdded] = useState<number[]>([]);
  const [removed, setRemoved] = useState<number[]>([]);

  const oldCards: CardValue[] = useMemo(
    () => (debugMode ? [8, 7, 7, 3, -11, 4, -10, 10, 6, 8] : (cards?.[round - 1] ?? [])),
    [cards, debugMode, round],
  );
  const newCards: CardValue[] = useMemo(
    () => (debugMode ? [8, 9, 9, 3, -12, 4, 10, -10, 6, 8] : (cards?.[round] ?? [])),
    [cards, debugMode, round],
  );

  const removedCards: CardValue[] = useMemo(
    () => (debugMode ? [-12, -12] : ownHorseRemovedCards),
    [debugMode, ownHorseRemovedCards],
  );

  const cardCount =
    newCards.length + Math.max(removedCards.length, currentBonusCards.length > 0 ? 1 : 0);
  const cardWidth = 80 / cardCount; // 80% of screen width => 10% padding left+right

  const showModal =
    !isSpectatorMode &&
    !isHistoryMode &&
    (debugMode ||
      (currentState === Turfmaster.stateMachine.MERGECARDS && currentPlayerId === playerId));
  const [modalMinimized, setModalMinimized] = useState(false);

  const onAccept = useCallback(() => {
    if (added.length === removed.length) {
      dispatch(
        sendMergeCards({
          playerid: playerId,
          horseid: currentHorse?.index, // TODO: undefined check has to be forced
          added,
          removed,
        }),
      );

      // modal is reusing ui so reset for merge in next round
      setAdded([]);
      setRemoved([]);
    }
  }, [added, removed, dispatch, playerId, currentHorse?.index]); // TODO: undefined check has to be forced

  const onBonusCardClicked = useCallback(() => {
    gameLogger.debug('onBonusCardClicked');
    dispatch(sendAddBonusCards({ playerid: playerId, horseid: currentHorse?.index })); // TODO: undefined check has to be forced
  }, [currentHorse?.index, dispatch, playerId]); // TODO: undefined check has to be forced

  useKeyPress(
    13,
    useCallback(() => {
      if (showModal && !modalMinimized) onAccept();
    }, [modalMinimized, onAccept, showModal]),
  );

  return (
    <>
      <Modal
        className="merge-modal pointer-events-auto"
        open={showModal && !modalMinimized}
        disablePortal
        disableAnimation
        description="Card merge interface for selecting cards to add and remove"
      >
        <div className="flex h-full w-full flex-col items-center justify-center focus:outline-hidden">
          <div className="mx-2 mb-4 flex w-full flex-row items-end justify-center">
            <CardRow
              cards={oldCards}
              isSelected={({ index }) => added.includes(index)}
              onCardClicked={({ index }) => {
                setAdded((prev) =>
                  prev.includes(index)
                    ? prev.filter((i) => i !== index)
                    : sortedUniq(sortBy([...prev, index])),
                );
              }}
              color={currentHorse?.color} // TODO: undefined check has to be forced
              {...{ removedCards, cardWidth, onBonusCardClicked }}
            />
          </div>
          <ButtonSuccess
            className={cn('h-8 w-8 transform-gpu cursor-pointer transition-all active:scale-95', {
              'opacity-30': added.length !== removed.length,
            })}
            onClick={onAccept}
          />
          <div className="mx-2 mt-4 flex w-full flex-row items-start justify-center">
            <CardRow
              cards={newCards}
              isSelected={({ index }) => !removed.includes(index)}
              onCardClicked={({ index }) =>
                setRemoved((prev) =>
                  prev.includes(index)
                    ? prev.filter((i) => i !== index)
                    : sortedUniq(sortBy([...prev, index])),
                )
              }
              bonusCardsEnabled={currentBonusCards.length > 0}
              color={currentHorse?.color ?? 0} // // TODO: undefined check has to be forced
              {...{ removedCards, cardWidth, onBonusCardClicked }}
            />
          </div>
        </div>
      </Modal>

      {showModal && (
        <ViewSwitchButton isMinimized={modalMinimized} setMinimized={setModalMinimized} />
      )}
    </>
  );
};

export default MergeView;
